"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import Image from "next/image"
import * as Dialog from "@radix-ui/react-dialog"
import { ChevronLeft, ChevronRight, X } from "lucide-react"

import type { CourseGalleryItem } from "@/types/database"

import { Eyebrow } from "./primitives"

const FADE_MASK =
  "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)"

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

function subscribeReducedMotion(callback: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION_QUERY)
  mq.addEventListener("change", callback)
  return () => mq.removeEventListener("change", callback)
}

function useReducedMotion() {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false
  )
}

/**
 * Faixa de prints de depoimentos. Auto-rola sozinha (auto-advance via
 * requestAnimationFrame sobre scrollLeft, lista duplicada p/ loop contínuo) mas
 * também permite rolagem/arraste manual — o auto-scroll pausa no hover, arraste,
 * foco ou aba oculta. Clicar num print abre um lightbox (Radix Dialog) com a
 * imagem em tamanho real, legível, e navegação entre prints.
 * Com prefers-reduced-motion não há auto-scroll — só rolagem manual.
 */
export function PrintsMarquee({ prints }: { prints: CourseGalleryItem[] }) {
  const [paused, setPaused] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const reducedMotion = useReducedMotion()

  const scrollRef = useRef<HTMLDivElement>(null)
  const drag = useRef({
    active: false,
    startX: 0,
    startLeft: 0,
    moved: false,
    pointerId: -1,
  })

  const animated = !reducedMotion && prints.length > 2
  const items = animated ? [...prints, ...prints] : prints

  const lightboxOpen = activeIndex != null

  // Auto-advance suave; pausa em hover/arraste/lightbox/aba oculta.
  useEffect(() => {
    if (!animated) return
    const el = scrollRef.current
    if (!el) return
    let raf = 0
    const step = () => {
      if (!paused && !lightboxOpen && document.visibilityState === "visible") {
        el.scrollLeft += 0.4
        const half = el.scrollWidth / 2
        if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half
      }
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [animated, paused, lightboxOpen])

  // Navegação por teclado dentro do lightbox.
  const showPrint = useCallback(
    (dir: 1 | -1) => {
      setActiveIndex((i) =>
        i == null ? i : (i + dir + prints.length) % prints.length
      )
    },
    [prints.length]
  )

  // Arraste com mouse (touch já rola nativo via overflow-x-auto).
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return
    const el = scrollRef.current
    if (!el) return
    drag.current = {
      active: true,
      startX: e.clientX,
      startLeft: el.scrollLeft,
      moved: false,
      pointerId: e.pointerId,
    }
    // Não captura o ponteiro aqui: um clique simples deixaria de chegar ao
    // botão (o click iria pro elemento que capturou). A captura acontece só
    // depois que o arraste cruza o limiar, em onPointerMove.
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return
    const el = scrollRef.current
    if (!el) return
    const dx = e.clientX - drag.current.startX
    if (Math.abs(dx) > 4 && !drag.current.moved) {
      drag.current.moved = true
      // Captura só agora, quando virou arraste de fato, p/ manter o scroll
      // funcionando mesmo se o ponteiro sair do container.
      el.setPointerCapture(drag.current.pointerId)
    }
    el.scrollLeft = drag.current.startLeft - dx
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current.active) {
      scrollRef.current?.releasePointerCapture?.(e.pointerId)
    }
    drag.current.active = false
  }

  const activePrint = activeIndex != null ? prints[activeIndex] : null

  return (
    <div className="mt-10 sm:mt-12">
      <div className="flex items-center gap-3">
        <Eyebrow className="text-[#7fa3b1]">Mensagens que chegam</Eyebrow>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      <div
        ref={scrollRef}
        className="-mx-5 mt-5 cursor-grab [scrollbar-width:none] overflow-x-auto px-5 active:cursor-grabbing sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
        style={{
          maskImage: FADE_MASK,
          WebkitMaskImage: FADE_MASK,
          scrollSnapType: "x proximity",
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="flex w-max gap-3 pb-2 sm:gap-4">
          {items.map((print, i) => {
            const realIndex = i % prints.length
            const isClone = animated && i >= prints.length
            return (
              <button
                key={`${print.url}-${i}`}
                type="button"
                aria-hidden={isClone}
                tabIndex={isClone ? -1 : 0}
                aria-label={`Abrir depoimento: ${print.alt || "print de aluno"}`}
                onClick={() => {
                  if (drag.current.moved) return
                  setActiveIndex(realIndex)
                }}
                className="relative w-[172px] shrink-0 cursor-zoom-in [scroll-snap-align:start] overflow-hidden rounded-xl border border-white/10 transition-[border-color,transform] hover:border-white/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
                style={{ aspectRatio: "9 / 14" }}
              >
                <Image
                  src={print.url}
                  alt={print.alt || "Depoimento de aluno"}
                  fill
                  sizes="172px"
                  draggable={false}
                  className="object-cover"
                  style={{
                    objectPosition:
                      print.crop_x != null && print.crop_y != null
                        ? `${print.crop_x}% ${print.crop_y}%`
                        : undefined,
                  }}
                />
              </button>
            )
          })}
        </div>
      </div>

      <Dialog.Root
        open={lightboxOpen}
        onOpenChange={(open) => {
          if (!open) setActiveIndex(null)
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <Dialog.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") showPrint(1)
              if (e.key === "ArrowLeft") showPrint(-1)
            }}
          >
            <Dialog.Title className="sr-only">
              {activePrint?.alt || "Depoimento de aluno"}
            </Dialog.Title>

            <Dialog.Close
              aria-label="Fechar"
              className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/11 bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <X size={20} />
            </Dialog.Close>

            {prints.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Depoimento anterior"
                  onClick={() => showPrint(-1)}
                  className="absolute left-3 flex h-11 w-11 items-center justify-center rounded-full border border-white/11 bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-6"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  aria-label="Próximo depoimento"
                  onClick={() => showPrint(1)}
                  className="absolute right-3 flex h-11 w-11 items-center justify-center rounded-full border border-white/11 bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}

            {activePrint && (
              <Image
                key={activePrint.url}
                src={activePrint.url}
                alt={activePrint.alt || "Depoimento de aluno"}
                width={activePrint.width}
                height={activePrint.height}
                sizes="(max-width: 640px) 92vw, 560px"
                className="h-auto max-h-[90vh] w-auto max-w-[min(92vw,560px)] rounded-2xl border border-white/11 object-contain"
              />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
